// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useRef } from "react";
import { useApp } from "../lib/app.js";
import { useI18n } from "../lib/i18n.js";
import type { Notice } from "../lib/notifications.js";
import { useStore } from "../lib/store.js";

/**
 * Sends over-budget alerts to the phone. Only the browser can read the
 * numbers, so it works them out and hands the server just the sentence the
 * user's detail setting allows. The server sends each one once.
 */
export function PushRelay({ notices }: { notices: readonly Notice[] }) {
  const { store } = useApp();
  const { notify } = useStore(store);
  const { t, money, category } = useI18n();
  const sent = useRef(new Set<string>());

  useEffect(() => {
    const push = notify.push;
    if (!push || !notify.types.budgets) return;
    for (const n of notices) {
      if (n.kind !== "budget-over" || sent.current.has(n.id)) continue;
      sent.current.add(n.id);
      const body =
        push.detail === "generic"
          ? t("push.alert.generic")
          : push.detail === "titles"
            ? t("push.alert.titles", { name: category(n.category) })
            : t("push.alert.amounts", { name: category(n.category), used: money(n.used), budget: money(n.budget) });
      // A failed alert is not worth an error banner; the app still shows it.
      store.backend.pushAlert(n.id, body).catch(() => sent.current.delete(n.id));
    }
  }, [notices, notify.push, notify.types.budgets, store, t, money, category]);

  return null;
}
